/**
 * ВЕСЬ ТЕКСТ САЙТА ЖИВЁТ ЗДЕСЬ.
 *
 * Analogous to Android's strings.xml: components contain markup and logic
 * only, every visible string comes from this file. Edit the text here and the
 * page changes — no component needs to be touched.
 *
 * Layout mirrors the page itself:
 *   settings   — switches that change what the page says (not text itself)
 *   contacts   — email / links, reused by the sidebar, contacts page and CTA
 *   content.nav / sidebar / author / socials  — the left column
 *   content.headings   — the title block at the top of each section
 *   content.projects / stack / about / contact        — section bodies
 *   content.quote      — the CTA strip under the project grid
 *   content.ui         — buttons, aria-labels, plurals, dates
 *
 * Two strings live outside React and have to be edited by hand:
 * `public/index.html` (meta description, noscript) and `public/manifest.json`
 * (app name).
 */

import {
  BlogPost,
  Fact,
  NavItem,
  ProcessStep,
  Project,
  SectionHeading,
  SectionId,
  Social,
  StackGroup,
  Testimonial,
  TimelineEntry,
} from './types';

/** Screenshots live in /public/previews — see the README for how they are built. */
const shot = (name: string) => `${process.env.PUBLIC_URL}/previews/${name}.webp`;

/**
 * Blog pictures live in /public/blog. Drop a PNG/JPG into `blog_sources/` at
 * the repository root, run `python3 tools/build_previews.py`, and the file
 * becomes `public/blog/<same-name>.webp` — `img('canvas-problem')` points at it.
 */
export const img = (name: string) => `${process.env.PUBLIC_URL}/blog/${name}.webp`;

/* ── Settings ───────────────────────────────────────────────────────────── */

export const settings = {
  /**
   * Where the site is deployed, without a trailing slash. Crawlers do not
   * resolve relative og:image URLs, so this is what makes a link shared in
   * Telegram arrive with a picture instead of as a bare string. Used by
   * `public/index.html` and `public/sitemap.xml`, which are edited by hand —
   * change the domain here and in those two files together.
   */
  siteUrl: 'https://favian1967.site',
  /** GitHub account behind the live sidebar stats and the profile link. */
  githubUser: 'favian1967',
  /** Switches the sidebar status between "Свободен" and "Занят". */
  availableForWork: true,
  /** How long GitHub responses stay cached in localStorage. */
  statsCacheMinutes: 30,
};

/* ── Contacts ───────────────────────────────────────────────────────────── */

export const contacts = {
  email: 'dpastuhovj@gmail.com',
  telegram: 'https://t.me/Rafink',
  github: `https://github.com/${settings.githubUser}`,
  kwork: 'https://kwork.ru/user/favian19671',
};

/* ── Content ────────────────────────────────────────────────────────────── */

export const content = {
  /* Sidebar: navigation -------------------------------------------------- */
  /**
   * "Отзывы" is dropped from this list while `testimonials.items` is empty —
   * see `Sidebar.tsx`. A nav item leading to an empty page is worse than no
   * nav item, and the section is never decorated with an invented quote.
   */
  nav: [
    { id: 'projects', label: 'Проекты' },
    { id: 'stack', label: 'Стек' },
    { id: 'blog', label: 'Блог' },
    { id: 'about', label: 'Обо мне' },
    { id: 'testimonials', label: 'Отзывы' },
    { id: 'contact', label: 'Контакты' },
  ] as NavItem[],
  navLabel: 'Основная навигация',

  /* Sidebar: live status block ------------------------------------------- */
  sidebar: {
    statusTitle: 'Статус системы',
    statusSource: 'онлайн',
    statusSourceHint: 'Живые данные из GitHub API',
    stats: {
      projects: 'Проекты',
      contributions: 'Контрибуций за год',
      repos: 'Публичных репозиториев',
      availability: 'Для новых задач',
    },
    available: 'Свободен',
    busy: 'Занят',
    availableHint: 'Свободен для новых задач',
  },

  /* Sidebar: terminal ---------------------------------------------------- */
  terminal: {
    prompt: 'engineer@ideaprojects:~$',
    lines: ['docker compose up -d', 'сервис поднялся за 4 секунды'],
  },

  /* Sidebar: author card ------------------------------------------------- */
  author: {
    name: 'Дмитрий',
    role: 'Full-stack разработчик',
    /** Built from avatar.png in the repo root by tools/build_previews.py. */
    avatar: `${process.env.PUBLIC_URL}/avatar.webp` as string | null,
  },

  /* Sidebar + contacts page: social links -------------------------------- */
  socials: [
    {
      id: 'github',
      label: 'GitHub',
      href: contacts.github,
      handle: `@${settings.githubUser}`,
    },
    {
      id: 'telegram',
      label: 'Telegram',
      href: contacts.telegram,
      handle: `@${contacts.telegram.split('/').pop()}`,
    },
    {
      id: 'kwork',
      label: 'Kwork',
      href: contacts.kwork,
      handle: `@${contacts.kwork.split('/').pop()}`,
    },
  ] as Social[],

  /**
   * The offer block above the project grid.
   *
   * Projects is the landing section, and a grid of cards answers "что он
   * делал" while the visitor is still asking "что он сделает мне". This block
   * answers that first, and carries the availability marker for phones, where
   * the sidebar is collapsed into a menu.
   *
   * Every claim here is already made somewhere else on the page — the project
   * count, the response time, the remote-and-in-writing format. Keep it that
   * way: a promise that appears only in the sales block is the one that gets
   * checked and found missing.
   */
  offer: {
    availableEyebrow: 'Свободен для новых задач',
    busyEyebrow: 'Сейчас занят, но задачу обсудить можно',
    /**
     * The largest text on the page, and the page's `<h1>`. It names what the
     * visitor gets, not what the author is — "Фулстек" was a job category,
     * and a job category is not an offer.
     */
    title: 'Разрабатываю сервисы и скрипты на Java и Python',
    text: 'Могу собрать проект с нуля или подключиться к существующему: спроектировать базу данных и API, добавить авторизацию, платежи и внешние интеграции, сделать интерфейс и настроить деплой. После запуска могу поддерживать и развивать проект.',
    /**
     * The line that does the actual convincing, so it is counted from the
     * project list instead of typed: a number that can go stale is a number
     * that will.
     */
    proof: (services: string, verifiable: number) =>
      `Ниже — ${services}, собранных так. ${verifiable} из них можно открыть прямо сейчас: живое демо или исходный код.`,
    points: [
      'Не надо нанимать команду',
      'Код и репозиторий будут принадлежать полностью вам',
      'Отвечаю в течение дня',
      'Удалённо и в переписке, без обязательных созвонов',
    ],
    primary: 'Написать в Telegram',
    secondary: 'Почта',
    note: 'Опишите задачу в двух абзацах, обсудим вариант решения и сроки.',
  },

  /* Title block at the top of each section ------------------------------- */
  headings: {
    projects: {
      title: 'Проекты',
      // The offer block below carries the pitch, so the subtitle only points.
      subtitle: [
        'Что я собрал и как это устроено внутри.',
        'Откройте карточку — там задача, решения, стек и ссылки, которые можно проверить.',
      ],
    },
    stack: {
      title: 'Стек',
      subtitle: [
        'Инструменты, на которых я разрабатываю сервисы.',
        'Каждый пункт ссылается на проект, который на нём работает.',
      ],
    },
    blog: {
      title: 'Блог',
      subtitle: [
        'Разборы задач, на которых я застревал, и того, чем они кончились.',
        'Описал просто и подробно.',
      ],
    },
    about: {
      title: 'Обо мне',
      subtitle: ['Чем я могу быть полезен.'],
    },
    testimonials: {
      title: 'Отзывы',
      subtitle: [
        'Данный блок посвящён отзывам клиентов о моей работе.',
        'Каждый отзыв можно открыть на площадке, где он был оставлен.',
      ],
    },
    contact: {
      title: 'Контакты',
      subtitle: [
        'Telegram — самый быстрый способ, почта тоже работает.',
        'Отвечаю в течение дня.',
      ],
    },
  } as Record<SectionId, SectionHeading>,

  /* Projects ------------------------------------------------------------- */
  /**
   * `details` opens the case-study panel: `shots` are the real screens of the
   * project, `links` the live demo or the repository. A project without
   * `details` renders as a plain, non-clickable card.
   *
   * `links` is what does the convincing. Each one carries a `kind` — `demo`
   * for a running instance, `repo` for public source — and that single field
   * drives the badge on the card, the order of the links in the dialog and
   * the counted claim in the offer block above the grid. When a project has
   * no link for a reason, say the reason in `linksNote`: an explained absence
   * reads as discretion, an unexplained one as a bluff.
   */
  projects: [
    {
      id: 'bookflow',
      number: '01',
      title: 'BookFlow',
      subtitle: 'Мультитенантный SaaS для онлайн-записи',
      stack: ['Django', 'React', 'PostgreSQL', 'Celery', 'Stripe'],
      preview: shot('bookflow-card'),
      span: 4,
      details: {
        year: '2025',
        role: 'Один инженер на проекте: схема данных, бэкенд, интерфейс, деплой',
        overview:
          'Сервис онлайн-записи, где каждый клиент работает в своём пространстве, а данные тенантов изолированы на уровне базы, а не на уровне вежливости кода.',
        highlights: [
          'Изоляция тенантов через row-level security в PostgreSQL',
          'Защита от двойной записи на уровне базы: exclusion constraint и advisory lock',
          'Celery для напоминаний, отложенных задач и ретраев',
          'Stripe и ЮKassa за одной абстракцией — под два рынка сразу',
          'S3 / MinIO для загрузок и сгенерированных документов',
        ],
        shots: [
          {
            src: shot('bookflow-1'),
            alt: 'Главная страница BookFlow: заголовок и виджет записи с выбором времени',
          },
          {
            src: shot('bookflow-2'),
            alt: 'Панель владельца: тариф, шаги настройки, счётчики записей и календарь дня',
          },
        ],
        links: [
          {
            label: 'Живое демо',
            href: 'https://bookflow.favian1967.site',
            kind: 'demo',
          },
        ],
      },
    },
    {
      id: 'aurora-bank',
      number: '02',
      title: 'Aurora Bank',
      subtitle: 'Цифровой банкинг и учёт общих расходов',
      stack: ['Java', 'Spring Boot', 'React', 'PostgreSQL', 'Redis'],
      preview: shot('aurora-bank-card'),
      span: 4,
      details: {
        year: '2024',
        role: 'Бэкенд транзакционного ядра и интерфейс',
        overview:
          'Движение денег между счетами и учёт общих расходов — область, где повтор запроса, сделавший не то, стоит реальных денег.',
        highlights: [
          'Java и Spring Boot там, где корректность важнее скорости разработки',
          'Идемпотентные транзакции и расчёт долей в копейках, без плавающей точки',
          'PostgreSQL как основное хранилище операций, Liquibase для миграций',
          'Redis для кэша, рейт-лимитов и фоновых задач',
          'React и TypeScript в дашборде и админке',
        ],
        shots: [
          {
            src: shot('aurora-bank-1'),
            alt: 'Вход в Aurora Bank: карточки счетов и форма авторизации',
          },
          {
            src: shot('aurora-bank-2'),
            alt: 'Дашборд: общий баланс, график движения средств, быстрый перевод и лента операций',
          },
        ],
        links: [
          { label: 'Живое демо', href: 'https://bank.favian1967.site', kind: 'demo' },
          {
            label: 'Транзакционное ядро',
            href: 'https://github.com/favian1967/BankSystem',
            kind: 'repo',
          },
          {
            label: 'Учёт общих расходов',
            href: 'https://github.com/favian1967/Splitwise',
            kind: 'repo',
          },
        ],
      },
    },
    {
      id: 'tutorspace',
      number: '03',
      title: 'TutorSpace',
      subtitle: 'Платформа обучения с интервальным повторением',
      stack: ['Django', 'React', 'PostgreSQL', 'Telegram Bot', 'SM-2'],
      preview: shot('tutorspace-card'),
      span: 4,
      details: {
        year: '2026',
        role: 'Платформа целиком: бэкенд, интерфейс, бот',
        overview:
          'Платформа занятий, где расписание повторов считается алгоритмом, а не проставляется преподавателем вручную. Преподаватель собирает неделю, ученик видит только сегодняшнее задание.',
        highlights: [
          'SM-2 как алгоритм интервальных повторений',
          'Пять типов упражнений — от подстановки с автопроверкой до голосового монолога',
          'Задания открываются по датам в часовом поясе ученика',
          'React и TypeScript в интерфейсе занятий',
          'Telegram-бот для повторения слов и напоминаний',
        ],
        shots: [
          {
            src: shot('tutorspace-1'),
            alt: 'Главная страница TutorSpace: серия дней, задание на сегодня и план недели',
          },
          {
            src: shot('tutorspace-2'),
            alt: 'Кабинет ученика: задание дня, прогресс недели, серия и карточки словаря',
          },
        ],
        links: [
          { label: 'Живое демо', href: 'https://tutor.favian1967.site', kind: 'demo' },
          {
            label: 'Исходный код',
            href: 'https://github.com/favian1967/teacher_system',
            kind: 'repo',
          },
        ],
      },
    },
    {
      id: 'match',
      number: '04',
      title: 'МЭТЧ',
      subtitle: 'Агрегатор IT-вакансий из Telegram-каналов',
      stack: ['Python', 'Aiogram', 'PostgreSQL', 'SQLAlchemy', 'Docker'],
      preview: shot('match-card'),
      span: 4,
      details: {
        year: '2026',
        role: 'Сервис целиком: сбор данных, бот, лендинг',
        overview:
          'Коллектор круглосуточно читает публичные Telegram-каналы с вакансиями, а бот присылает человеку только то, что совпало с его фильтром по стеку, грейду и формату работы.',
        highlights: [
          'Два процесса: коллектор собирает каналы, бот доставляет совпадения',
          'Дедупликация по content_hash — один и тот же пост из пяти каналов приходит один раз',
          'Разбор свободного текста вакансии: стек и грейд вытаскиваются парсером под юнит-тестами',
          'Источник данных спрятан за интерфейсом: веб-превью t.me сейчас, Telethon позже без переписывания остального',
          'SQLAlchemy 2.0 async, миграции на Alembic, деплой через Docker Compose',
        ],
        shots: [
          {
            src: shot('match-1'),
            alt: 'Лендинг МЭТЧ: счётчик просканированных вакансий и конструктор фильтра',
          },
          {
            src: shot('match-2'),
            alt: 'Бот в Telegram: карточка вакансии со стеком, грейдом, форматом и ссылкой',
          },
        ],
        links: [
          { label: 'Живое демо', href: 'https://match.favian1967.site', kind: 'demo' },
          {
            label: 'Исходный код',
            href: 'https://github.com/favian1967/tg_parser',
            kind: 'repo',
          },
        ],
      },
    },
    {
      id: 'kwork-parser',
      number: '05',
      title: 'Kwork Parser',
      subtitle: 'Telegram-бот, который следит за Kwork.ru',
      stack: ['Python', 'AsyncIO', 'HTTPX', 'Telegram API', 'TOML'],
      preview: shot('kwork-parser-card'),
      span: 4,
      details: {
        year: '2026',
        role: 'Сервис целиком, от сбора данных до доставки в Telegram',
        overview:
          'Фоновый сборщик, который следит за новыми заказами и присылает подходящие в Telegram, не превращаясь при этом в источник блокировок.',
        highlights: [
          'AsyncIO и HTTPX: аккуратный конкурентный сбор данных с backoff',
          'Антиблокировка: случайные интервалы опроса, отступ при 403 и 429',
          'Telegram Bot API для доставки уведомлений и команд',
          'Конфигурация в TOML — рядом с кодом, но отдельно от него',
        ],
        shots: [
          {
            src: shot('kwork-parser-1'),
            alt: 'Бот в Telegram: заказы с бюджетом, описанием, автором и числом откликов',
          },
        ],
        links: [
          {
            label: 'Исходный код',
            href: 'https://github.com/favian1967/kwork_parser',
            kind: 'repo',
          },
        ],
      },
    },
    {
      id: 'gravitas',
      number: '06',
      title: 'GRAVITAS',
      subtitle: 'RAG-ассистент, отвечающий по своей библиотеке',
      stack: ['Python', 'RAG', 'ChromaDB', 'Streamlit', 'LLM API'],
      preview: shot('gravitas-card'),
      span: 4,
      details: {
        year: '2026',
        role: 'Пайплайн поиска, интерфейс и доступы',
        overview:
          'Чат, который отвечает на вопросы о продажах и переговорах по загруженной библиотеке книг, а не по общим знаниям модели — и показывает, из каких источников собран каждый ответ.',
        highlights: [
          'Пайплайн RAG: нарезка на чанки, эмбеддинги sentence-transformers, векторный поиск в ChromaDB',
          'Роутер намерения: «объясни» и «напиши сообщение» уходят в разные промпты',
          'Источники раскрываются под ответом — видно, откуда пришёл каждый кусок контекста',
          'История диалога учитывается при переформулировке запроса к базе',
          'Аккаунты, лимит бесплатных сообщений и промокоды',
        ],
        shots: [
          {
            src: shot('gravitas-1'),
            alt: 'Интерфейс GRAVITAS: список диалогов слева, ответ с разбором по пунктам справа',
          },
        ],
        wip: true,
        links: [],
        linksNote:
          'Мой собственный проект, ещё в разработке — поэтому демо и репозитория пока нет. Скриншот выше снят с рабочей сборки.',
      },
    },
    {
      id: 'tg-sheets-parser',
      number: '07',
      title: 'TG → Sheets',
      subtitle: 'Бот, переносящий сообщения из Telegram-чатов в Google Sheets',
      stack: ['Python', 'Telegram Bot API', 'Google Sheets API', 'SQLite'],
      preview: shot('tg-sheets-parser-card'),
      span: 4,
      details: {
        year: '2026',
        role: 'Сервис целиком: приём сообщений, очередь, запись в таблицу, деплой',
        overview:
          'Бот круглосуточно собирает сообщения групповых чатов Telegram и пишет их в Google-таблицу — один чат, один лист — без дублей и без потерь при обрывах связи.',
        highlights: [
          'Очередь операций в SQLite (WAL): ничего не теряется при падении сервера или разрыве связи',
          'Идемпотентная запись — при сбое пачка сверяется с таблицей заново, дублей не бывает',
          'Правки сообщений обновляют строку на месте, альбомы пишутся одной строкой',
          'Добавление нового чата не требует настройки: лист создаётся сам при первом сообщении',
          'Уведомления и команды владельцу в личке: статус, список чатов, ручной ретрай',
        ],
        shots: [
          {
            src: shot('tg-sheets-parser-1'),
            alt: 'Google-таблица со строками сообщений рядом с исходным чатом в Telegram',
          },
        ],
        links: [],
        // The one project on this page built to order, which is exactly why it
        // is the one without a link. Said plainly, that reads as discretion.
        linksNote:
          'Сделано на заказ — код и доступы у заказчика, поэтому ни репозитория, ни демо здесь нет. Как это устроено внутри, расскажу в переписке.',
      },
    },
  ] as Project[],

  /* Social proof under the project grid ----------------------------------- */
  /**
   * The block renders only while `items` has something in it, so the page is
   * never decorated with an empty "Отзывы" heading — and never with an
   * invented quote. Two real lines from two real clients, with names and
   * links, outweigh everything else on this site. Ask for them; people who
   * were happy with the work usually say yes.
   */
  testimonials: {
    title: 'Отзывы',
    /**
     * Quoted exactly as written, punctuation and all. The second review calls
     * the order «система записи с ии» — that is the client describing what
     * they ordered, and it stays in their quote untouched. The `role` line
     * next to it says what was delivered, and it does not claim AI, because
     * the classifier in that project is a prepared seam, not a shipped
     * feature. Never let a `role` promise more than the code does.
     */
    items: [
      {
        text: 'Надо было подключить чаты ТГ к гугл таблицам, справились идеально, сделали всё как указывал в тз, работает отлично, работа была проделана оперативно, заказ был выполнен в срок, после, объяснили процесс работы и всё подробно расписали.',
        author: 'SkandinavskiyBog',
        role: 'Telegram-бот, переносящий чаты в Google Sheets — проект TG → Sheets ниже',
        date: '2026-08',
        href: 'https://kwork.ru/user/favian19671',
      },
      {
        text: 'Нужно было создать систему записи с ии. Все работает шикарно. Спасибо большое! Работаем дальше',
        author: 'maksimilian_chel',
        role: 'Telegram-бот записи в автосервис с веб-админкой',
        date: '2026-07',
        href: 'https://kwork.ru/user/favian19671',
      },
    ] as Testimonial[],
  },

  /* CTA strip under the project grid ------------------------------------- */
  /**
   * Rendered at the end of every section except contacts, which is one long
   * CTA already. A visitor who reads a section to the end is the most
   * interested visitor on the site, and until now three of the four sections
   * ended with nothing to click.
   */
  quote: {
    text: [
      'Каждый проект здесь я вёл полностью один.',
      'Опишите задачу в двух абзацах, обсудим вариант решения и сроки.',
    ],
    cta: 'Написать в Telegram',
  },

  /* Stack ---------------------------------------------------------------- */
  stack: {
    /**
     * Every entry names the projects it is actually used in — the tags on the
     * project cards are the source of truth, so nothing here is a claim
     * without evidence somewhere else on the page.
     */
    groups: [
      {
        id: 'backend',
        title: 'Бэкенд',
        caption: 'Серверная часть: бизнес-логика и API',
        items: [
          {
            name: 'Python',
            note: 'Основной язык: сервисы, боты, парсеры',
            usedIn: ['BookFlow', 'TutorSpace', 'МЭТЧ', 'Kwork Parser', 'GRAVITAS', 'TG → Sheets'],
          },
          {
            name: 'Django',
            note: 'Мультитенантные приложения, админка, работа через ORM',
            usedIn: ['BookFlow', 'TutorSpace'],
          },
          {
            name: 'SQLAlchemy',
            note: 'Асинхронный доступ к базе и миграции вне Django-мира',
            usedIn: ['МЭТЧ'],
          },
          {
            name: 'Java / Spring Boot',
            note: 'Быстрые и защищённые транзакционные сервисы',
            usedIn: ['Aurora Bank'],
          },
          {
            name: 'Celery',
            note: 'Отложенные задачи, уведомления, ретраи',
            usedIn: ['BookFlow'],
          },
        ],
      },
      {
        id: 'frontend',
        title: 'Фронтенд',
        caption: 'Интерфейсы, дизайн, адаптивность под разные экраны',
        items: [
          {
            name: 'React',
            note: 'Дашборды, сценарии записи, админ-панели, лендинги',
            usedIn: ['BookFlow', 'Aurora Bank', 'TutorSpace', 'МЭТЧ'],
          },
          {
            name: 'TypeScript',
            note: 'Типизация от пропсов до контрактов API',
            usedIn: ['Aurora Bank', 'TutorSpace'],
          },
          {
            name: 'Recharts',
            note: 'Рабочие графики в дашбордах',
            usedIn: ['Aurora Bank'],
          },
        ],
      },
      {
        id: 'data',
        title: 'Данные и инфраструктура',
        caption: 'Хранение, кэширование и доставка',
        items: [
          {
            name: 'PostgreSQL',
            note: 'Основное хранилище данных приложения',
            usedIn: ['BookFlow', 'Aurora Bank', 'TutorSpace', 'МЭТЧ'],
          },
          {
            name: 'Redis',
            note: 'Кэш, рейт-лимиты, брокер для фоновых задач',
            usedIn: ['Aurora Bank', 'BookFlow'],
          },
          {
            name: 'Docker',
            note: 'Один и тот же образ для всех устройств и серверов',
            usedIn: ['BookFlow', 'Aurora Bank', 'TutorSpace', 'МЭТЧ'],
          },
          {
            name: 'S3 / MinIO',
            note: 'Загрузки пользователей и сгенерированные документы',
            usedIn: ['BookFlow'],
          },
          {
            name: 'SQLite',
            note: 'Очередь на диске: гарантия доставки без внешней СУБД',
            usedIn: ['TG → Sheets'],
          },
        ],
      },
      {
        id: 'integrations',
        title: 'Боты и интеграции',
        caption: 'Где продукт встречается с внешним миром',
        items: [
          {
            name: 'Aiogram',
            note: 'Telegram-боты с диалогами на конечных автоматах',
            usedIn: ['МЭТЧ'],
          },
          {
            name: 'Telegram Bot API',
            note: 'Уведомления и лёгкие админские сценарии',
            usedIn: ['Kwork Parser', 'TutorSpace', 'TG → Sheets'],
          },
          {
            name: 'Google Sheets API',
            note: 'Таблица как интерфейс для нетехнического заказчика',
            usedIn: ['TG → Sheets'],
          },
          {
            name: 'Stripe / ЮKassa',
            note: 'Платежи, подписки, сверка вебхуков',
            usedIn: ['BookFlow'],
          },
          {
            name: 'AsyncIO / HTTPX',
            note: 'Аккуратный конкурентный сбор данных с backoff',
            usedIn: ['МЭТЧ', 'Kwork Parser'],
          },
          {
            name: 'RAG: ChromaDB и эмбеддинги',
            note: 'Ответы по своей базе документов, со ссылкой на источник',
            usedIn: ['GRAVITAS'],
          },
        ],
      },
    ] as StackGroup[],
  },

  /* Blog ----------------------------------------------------------------- */
  /**
   * Long-form write-ups. A feed of cards, and a click opens the article as a
   * single column, addressable at `#/blog/<id>`.
   *
   * TO ADD A POST
   *   1. Copy the object below to the top of `posts` — newest first.
   *   2. Change `id` (it is the URL — keep it stable once published), `title`,
   *      `excerpt`, `date`, `tags`.
   *   3. Assemble `body` out of blocks. The full catalogue, with examples of
   *      every block and every infographic, is in the README under "Blog".
   *   4. Pictures: drop the file into `blog_sources/` at the repository root,
   *      run `python3 tools/build_previews.py`, and reference it as
   *      `img('<filename without extension>')`.
   *
   * An empty `posts` removes the section from the nav entirely, same as
   * reviews. The post below is real: it is about code that sits in
   * `tools/build_previews.py` in this repository, and every number in it was
   * measured rather than invented — which is the standard the next one has to
   * meet too. Delete it once you have written your own.
   */
  blog: {
    /** While `posts` is empty the section is unreachable anyway — a safety net. */
    empty: 'Первый разбор в работе.',

    posts: [
      {
        id: 'double-spending-race-condition',
        title: 'Проблема двойных списаний в цифровых продуктах',
        excerpt:
          'Делал банковский сервис: счета, карты, переводы, валюты. Логика перевода выглядела очевидной — проверил баланс, списал, зачислил. Под нагрузкой два одновременных перевода со счёта на 100 рублей прошли оба, и баланс ушёл в минус. Разбираю, почему так происходит, как это чинится атомарной операцией и блокировкой строки, и откуда рядом берётся deadlock.',
        date: '2026-08-31',
        tags: ['Race condition', 'PostgreSQL', 'Платежи'],
        body: [
          {
            type: 'text',
            text: 'Когда я делал банковский сервис — счета, карты, переводы между пользователями, разные валюты. По сути, свой мини-банк, только под конкретную задачу клиента. Обычная логика перевода: проверяем баланс, если денег хватает — списываем и зачисляем получателю.',
          },
          {
            type: 'text',
            text: 'Всё работало идеально. И будет работать — пока не начнутся тесты.',
          },
          { type: 'heading', text: 'Проверка на прочность' },
          {
            type: 'text',
            text: 'Перед сдачей я всегда гоняю подобное под нагрузкой: пишу скрипт, который спамит API десятками запросов одновременно. Для перевода денег это база. В реальной жизни человек может дважды тапнуть кнопку «Отправить», клик может продублироваться из-за лагов сети, или кто-то откроет приложение в двух вкладках.',
          },
          {
            type: 'text',
            text: 'Пишем скрипт. На счёте 100 рублей, отправляю два запроса на перевод по 100 рублей в один и тот же момент — имитируя случайный дабл-клик в меню банка.',
          },
          {
            type: 'quote',
            text: 'Оба прошли. Баланс: минус 100.',
          },
          {
            type: 'text',
            text: 'Очевидно, что в банке на обычном счету не может быть минус 100 рублей.',
          },
          { type: 'heading', text: 'Почему так произошло' },
          {
            type: 'text',
            text: 'Логика перевода выглядела логично: сначала читаем баланс из базы, проверяем, хватает ли денег, если да — списываем. Три отдельных шага. Проблема в том, что между шагом «прочитать баланс» и шагом «списать» проходит какое-то время — пусть и доли секунды, но за него может успеть проскочить второй запрос.',
          },
          {
            type: 'list',
            ordered: true,
            items: [
              'Оба запроса читают баланс в ОДИН момент',
              'Оба видят «100 рублей есть»',
              'Оба решают, что перевод разрешён',
              'Оба списывают',
            ],
          },
          {
            type: 'text',
            text: 'База не в курсе, что происходит два перевода одновременно. С её точки зрения это просто два независимых запроса, каждый из которых сам по себе корректен.',
          },
          {
            type: 'note',
            tone: 'warn',
            title: 'Race condition — гонка условий',
            text: 'Штука коварная тем, что в обычном ручном тестировании она не ловится вообще. Ты физически не можешь кликнуть по кнопке дважды с разницей в миллисекунды. Она вылезает только под настоящей нагрузкой — то есть ровно тогда, когда сервис уже в работе и ошибки уже дороги.',
          },
          { type: 'heading', text: 'Решение' },
          {
            type: 'text',
            text: 'Сначала убираем «читай, потом пиши» в одну атомарную операцию. Вместо того чтобы сначала проверять баланс отдельным запросом, а потом отдельным запросом списывать, проверка и списание стали одной неделимой командой на уровне базы данных: «спиши сумму, если баланс после списания не уйдёт в минус». База сама гарантирует, что это выполнится либо целиком, либо не выполнится вовсе — без промежутка, в который может влезть второй запрос.',
          },
          {
            type: 'text',
            text: 'Дальше — блокировка на уровне строки. Пока один перевод обрабатывает конкретный счёт, база придерживает эту строку, и второй запрос к тому же счёту ждёт разблокировки. Ошибочные списания становятся невозможны.',
          },
          {
            type: 'compare',
            before: {
              title: 'Было',
              items: [
                'Читаем баланс отдельным запросом',
                'Проверяем в коде, хватает ли денег',
                'Списываем третьим запросом',
                'Между шагами успевает влезть второй перевод',
              ],
            },
            after: {
              title: 'Стало',
              items: [
                'Проверка и списание — одна атомарная команда базы',
                'Строка счёта блокируется на время перевода',
                'Второй запрос ждёт, а не работает по устаревшим данным',
                'Баланс физически не может уйти в минус',
              ],
            },
          },
          {
            type: 'text',
            text: 'Прогнал ту же атаку снова: 50 одновременных запросов на счёт со 100 рублями. Прошёл ровно один перевод, остальные корректно отклонены с «недостаточно средств». Баланс не уходит в минус, сколько бы запросов ни прилетело.',
          },
          {
            type: 'stats',
            items: [
              { value: '50', label: 'одновременных запросов', hint: 'на счёт со 100 рублями' },
              { value: '1', label: 'перевод прошёл', hint: 'остальные отклонены корректно' },
              { value: '0', label: 'уходов в минус', hint: 'и ни одного зависания' },
            ],
          },
          { type: 'heading', text: 'Рядом живёт ещё одна проблема' },
          {
            type: 'text',
            text: 'Если перевод идёт «туда-обратно» — А переводит Б, а в этот же момент Б переводит А, — обе операции пытаются заблокировать сразу два счёта, но в разном порядке. Первая держит счёт А и ждёт счёт Б, вторая держит счёт Б и ждёт счёт А. Обе ждут друг друга вечно. Это называется deadlock.',
          },
          {
            type: 'text',
            text: 'Почти классическая и довольно очевидная ошибка — если знать про неё заранее. Чинится за час: я ввожу единый порядок блокировки счетов, например всегда сначала блокируется счёт с меньшим ID, независимо от того, кто отправитель. Тогда обе операции блокируют счета в одном и том же порядке, и гонка исчезает.',
          },
          { type: 'heading', text: 'Итог' },
          {
            type: 'text',
            text: 'Сервис прошёл нагрузочное тестирование на параллельные переводы без единого случая ухода в минус и без единого зависания.',
          },
          {
            type: 'text',
            text: 'Если у вас есть сервис, где деньги, баллы или лимиты меняются от действий нескольких пользователей одновременно, там почти наверняка есть похожая гонка — просто она ещё не успела проявиться на реальных пользователях.',
          },
        ],
      },
      {
        id: 'telegram-sheets-duplicates',
        title:
          'Клиент написал телеграм-бота через нейросеть. Каждое сообщение попадало в таблицу дважды',
        excerpt:
          'Бот клиента дублировал через одно сообщение в Google Таблице — казалось, баг в коде. На деле Telegram доставляет обновления «хотя бы один раз», а любой медленный сетевой вызов внутри бота превращает эту гарантию в дубль. Разбираю, как это лечится подтверждением, идемпотентностью и записью пачками.',
        date: '2026-08-24',
        tags: ['Telegram-боты', 'Google Sheets', 'Идемпотентность'],
        body: [
          {
            type: 'text',
            text: 'Ко мне пришёл человек с компанией, которая занимается поставками в Китай. Вся рабочая координация у них живёт в тг-чате: заявки, статусы отгрузок, вопросы вида «а этот заказ ушёл или нет». Обычная история — чат удобен, пока в нём десять сообщений в день.',
          },
          {
            type: 'text',
            text: 'Проблема начиналась в конце месяца. Нужно было понять, сколько было заявок, где застои, кто на что отвечал дольше всего. В тг это не смотрится никак. Листать три тысячи сообщений вверх — не очень удобно.',
          },
          {
            type: 'text',
            text: 'Задача звучала просто: пусть всё, что происходит в чате, автоматически ложится в таблицу. Дальше он сам будет фильтровать, посчитает, построит сводную.',
          },
          { type: 'heading', text: 'Он сначала попробовал сам' },
          {
            type: 'text',
            text: 'Хорошо учиться чему-то новому — вот и клиент пробовал. Он описал задачу нейросети, получил код бота за вечер, поднял его. Бот заработал.',
          },
          {
            type: 'text',
            text: 'И начал дублировать. Человек пишет в чат одно сообщение, а в таблице появляются две строки. Не всегда, но регулярно. Вся аналитика после этого бессмысленна: любой подсчёт завышен на непонятную величину.',
          },
          {
            type: 'text',
            text: 'Он потратил на это несколько вечеров, просил модель починить, получал разные версии кода — дубликаты оставались. Помимо этого была проблема с получением и структуризацией изображений, а также обработкой более специфичных сценариев. После чего пошёл искать человека.',
          },
          { type: 'heading', text: 'Почему так происходит' },
          {
            type: 'text',
            text: 'Тут интересное, и я потому этот кейс и пишу.',
          },
          {
            type: 'quote',
            text: 'Бот не отправлял ничего дважды. Ему присылали дважды.',
          },
          {
            type: 'text',
            text: 'Telegram доставляет обновления боту по принципу «хотя бы один раз». Не ровно один раз, а именно как минимум один или больше. Если сервер бота не подтвердил приём вовремя, Telegram считает доставку неудачной и присылает то же самое обновление снова. Это не баг, это сознательное проектное решение: лучше доставить дважды, чем потерять — такой паттерн применяется во многих системах, где терять данные недопустимо.',
          },
          {
            type: 'text',
            text: 'А теперь смотрим, что делал бот. Пришло сообщение → бот идёт писать строку в таблицу → запись в таблицу — это сетевой вызов к чужому API, он занимает от сотен миллисекунд до нескольких секунд → и только после этого бот отвечает «принял».',
          },
          {
            type: 'text',
            text: 'Если таблица в этот момент отвечала медленно, бот не успевал подтвердить. Telegram присылал обновление повторно. Бот честно писал вторую строку.',
          },
          {
            type: 'text',
            text: 'То есть дубликаты появлялись тогда, когда таблица тормозила. Поэтому они и были нерегулярными, и поэтому их так тяжело было поймать.',
          },
          {
            type: 'text',
            text: 'И отдельно про нейросеть, раз уж все сейчас так пишут код. Модель написала корректный код для той задачи, которую ей поставили. «Принимай сообщения и записывай в таблицу» — она ровно это и сделала. Про то, что транспорт под ней даёт доставку хотя бы один раз, в задаче не было ни слова. Это не тупость модели, а скорее вопрос опыта: если вы однажды на подобное натыкались, повторно на те же грабли наступить сложно.',
          },
          {
            type: 'text',
            text: 'Такие вещи будут вылезать всё чаще. Код теперь пишется быстро, а знание о том, где у конкретного протокола острые углы, быстро не появляется.',
          },
          { type: 'heading', text: 'Что мы сделали' },
          {
            type: 'text',
            text: 'Сначала подтверждение, потом работа. Бот отвечает Telegram «принял» немедленно, а запись в таблицу уходит в отдельную очередь и происходит после. Telegram больше не ждёт таблицу и не имеет повода ретраить. Это убрало основную причину.',
          },
          {
            type: 'text',
            text: 'Отсеивание по идентификатору. Причину убрали, но повтор может прилететь и по другим поводам: сеть моргнула, бот перезапустился. Поэтому у каждого обновления от Telegram есть свой номер, и бот теперь помнит, какие номера он уже обработал. Повтор просто отбрасывается. Дополнительно ключ проверяется по паре «чат + сообщение» — на случай пересылок и правок.',
          },
          {
            type: 'note',
            tone: 'good',
            title: 'Идемпотентность — не украшение',
            text: 'Обработай хоть десять раз — результат один. В системах, где данные потом считают, это обязательное свойство, а не украшение: подобное используется практически во всех топовых системах.',
          },
          {
            type: 'text',
            text: 'Запись пачками. Строки уходят в таблицу не по одной, а группой раз в несколько секунд. Когда в чате всплеск на пятьдесят сообщений, поштучная запись упирается в лимиты API таблицы и начинает отваливаться. Пачками — не упирается.',
          },
          {
            type: 'text',
            text: 'Развернул и настроил хостинг я сам. На финальном созвоне мы вместе прогнали чат и посмотрели, как строки приезжают в таблицу.',
          },
          { type: 'heading', text: 'Что сломалось потом' },
          { type: 'text', text: 'Через день бот лёг.' },
          {
            type: 'text',
            text: 'Не из-за кода. На сервере просто кончилось место на диске. Логи писались без всякого ограничения, VPS дешёвый, диск маленький, и в какой-то момент писать стало некуда — процесс умер.',
          },
          {
            type: 'text',
            text: 'Это мой недосмотр, целиком. Я думал про корректность данных, про дубликаты, про лимиты чужого API — и не подумал про то, что процесс живёт на машине с конечным диском. Ограничение размера логов делается одной строчкой конфига, я его просто не поставил.',
          },
          {
            type: 'text',
            text: 'Поставил, добавил ротацию, оставил запас. С тех пор работает.',
          },
          {
            type: 'note',
            tone: 'warn',
            text: 'Мораль тут занудная, но за неё платят: у маленьких проектов падения почти никогда не связаны с алгоритмами. Они связаны со скучными вещами — кончилось место, кончилась память, умер токен, уткнулись в лимит стороннего API. Разработчик, который думает об этом заранее, отличается от того, кто не думает, примерно на одну строчку конфига и один ночной звонок.',
          },
          { type: 'heading', text: 'О чём я отказался' },
          {
            type: 'text',
            text: 'Клиент спросил, можно ли то же самое для WeChat — у них там основная переписка с китайской стороной.',
          },
          { type: 'text', text: 'Разобравшись в теме, я отказался.' },
          {
            type: 'text',
            text: 'Не потому что технически сложно. Потому что там основная работа не в коде: доступ к API официального аккаунта упирается в верификацию, а она завязана на местное юрлицо и документы. Это месяцы бумажной работы на стороне клиента, и результат от меня почти не зависит.',
          },
          {
            type: 'text',
            text: 'Я мог взять деньги и потом полгода объяснять, почему ничего не движется. Проще было сказать сразу.',
          },
          { type: 'heading', text: 'Итог' },
          {
            type: 'list',
            items: [
              'Дубликатов нет, система работает корректно',
              'Ручной разбор чата в конце месяца: было ~5 часов, стало — меньше часа (в будущем планируем обрабатывать данные через ИИ-аналитику)',
              'Работа заняла 5 дней с правками',
            ],
          },
          {
            type: 'text',
            text: 'Если у вас переписка с клиентами или подрядчиками живёт в телеграме и в конце месяца её невозможно посмотреть цифрами — это чинится примерно за неделю. Напишите, скажу, стоит ли оно того в вашем случае.',
          },
        ],
      },
    ] as BlogPost[],
  },

  /* About ---------------------------------------------------------------- */
  about: {
    /**
     * The one block on the site containing claims that cannot be verified
     * from the code or from GitHub — everything else is derived from real
     * data. Keep the dates and the timeline honest.
     */
    intro: [
      'Фулстек. Питон и Джава, на фронте React.',
      'Решаю задачи автоматизации и рутинных процессов в бизнесе: там, где работу до сих пор делают руками, её начинает делать сервис.',
      'Делаю один. Схему, бэк, фронт, деплой. После запуска остаюсь чинить, если что-то отвалится (бесплатные правки по багам в течение 14 дней после сдачи заказа).',
      'Сейчас разбираюсь с машинным обучением и нейронными сетями: векторные представления и поиск по эмбеддингам, дообучение моделей под конкретную задачу и интеграция LLM в продакшн-сервисы.',
    ],

    factsTitle: 'Коротко',
    facts: [
      {
        label: 'Фокус',
        value:
          'Бэкенд и full-stack продуктовая разработка: SaaS, Telegram-боты с админкой и оплатой, внутренние системы для автоматизации ручной работы',
      },
      {
        label: 'Стек',
        value:
          'Python (Django, aiogram, SQLAlchemy), Java (Spring Boot), TypeScript и React на фронте, PostgreSQL, Redis, Docker',
      },
      {
        label: 'Формат',
        value:
          'Удалённо, переписка в Telegram или почте, без обязательных ежедневных созвонов',
      },
      {
        label: 'Клиенты',
        value: 'Основатели и небольшие команды, где нужен один инженер на весь продукт',
      },
    ] as Fact[],

    timelineTitle: 'Путь',
    timelineCaption: 'Как работа пришла к тому, что есть сейчас',
    timeline: [
      {
        period: '2026',
        title: 'Full-stack разработчик: фриланс и свои продукты',
        body: 'Системы записи, Telegram-боты и автоматизация для небольшого бизнеса. Разработка собственных сложных, многофункциональных и распределённых систем, интеграция и обучение ИИ в сервисах и продуктах.',
      },
      {
        period: '2025',
        title: 'Python: автоматизация, скрипты и боты',
        body: 'Изучил Python для автоматизации, скриптов и ботов в Telegram. Собрал BookFlow — мультитенантный SaaS для онлайн-записи, а рядом TutorSpace, МЭТЧ, GRAVITAS, Kwork Parser и TG → Sheets.',
      },
      {
        period: '2024',
        title: 'Переход на JVM',
        body: 'Взялся за Java и Spring Boot и начал разрабатывать банковскую систему: транзакционное движение денег, идемпотентность, миграции и сложные защитные механизмы против ошибок, которые в будущем стоят реальных денег.',
      },
      {
        period: '2023',
        title: 'Первые проекты',
        body: 'Разрабатывал Android-приложения на Java — первые проекты, доведённые от идеи до работающего продукта.',
      },
    ] as TimelineEntry[],

    currentlyTitle: 'Сейчас',
    currently: [
      'Разрабатываю собственный ИИ для продаж и свой крипто-продукт',
      'Пишу очень много тестов, которые показывают работоспособность продукта',
      'Ещё больше погружаюсь в сложные распределённые системы и занимаюсь автоматизацией и разработкой сайтов и продуктов для малого и среднего бизнеса',
    ],

    /**
     * Kept out of `currently` on purpose: what I am learning and what I am
     * available for are two different statements, and mixing them into one
     * list made the second one read as a footnote to the first.
     */
    openTitle: 'Открыт к работе',
    open: [
      'Открыт к задачам на бэкенде, фронтенде и full-stack',
      'Готов взяться за Telegram-ботов, Python-скрипты и парсинг данных',
    ],
  },

  /* Contacts page -------------------------------------------------------- */
  /**
   * This is the page where a visitor decides, so it stays short: the contact
   * channels, and what happens after the first message.
   *
   * `process` is a promise made on your behalf — read it once and make sure
   * you actually work this way. A promise here that you break later costs
   * more than the client it won.
   */
  contact: {
    heroTitle: 'Напишите мне',
    heroText:
      'Расскажите, в чём заключается проблема или запрос. Мы это обсудим и подберём индивидуальное решение.',
    telegram: 'Написать в Telegram',
    /** The address itself is the button: a click copies it. */
    copyHint: 'Нажмите, чтобы скопировать',
    copied: 'Скопировано',

    processTitle: 'Как проходит работа',
    processCaption: 'Что происходит после того, как вы напишете',
    process: [
      {
        number: '01',
        title: 'Разбор задачи',
        body: 'Списываемся в Telegram или почте. Обсуждаем задачу, я задаю вам вопросы, и мы разбираем варианты решения вашей задачи.',
      },
      {
        number: '02',
        title: 'План и смета',
        body: 'Присылаю, что будет сделано, в каком порядке и за какой срок. Информацию сразу фиксируем, чтобы не было недопонимания.',
      },
      {
        number: '03',
        title: 'Работа частями',
        body: 'Показываю результат по ходу работы и задаю уточняющие вопросы, если надо, — так вы вносите правки заранее.',
      },
      {
        number: '04',
        title: 'Передача',
        body: 'Отдаю вам код, доступы и инструкцию, как развернуть проект с нуля. Дальше либо вы сами, либо я на поддержке. Проект полностью переходит под ваш контроль.',
      },
    ] as ProcessStep[],
  },

  /* Buttons, aria-labels, formats ---------------------------------------- */
  ui: {
    /**
     * Browser tab title; `{section}` is the heading of the open section. The
     * trailing half carries the words someone would actually search for. The
     * site is a person, not a studio, so the name is the person's. Keep the
     * static `<title>` in `public/index.html` matching the landing section.
     */
    documentTitle: (section: string) =>
      `${section} — ${content.author.name}, full-stack разработчик`,

    viewLabel: 'Вид',
    viewGroupLabel: 'Режим отображения',
    viewGrid: 'Сеткой',
    viewList: 'Списком',
    themeToLight: 'Переключить на светлую тему',
    themeToDark: 'Переключить на тёмную тему',

    cardPreviewAlt: (project: string) => `Превью проекта ${project}`,
    cardOpen: (project: string) => `Открыть проект ${project}`,

    /**
     * Shown on the card itself, not only inside the case study: a visitor
     * scanning the grid should see which projects they can verify before
     * deciding whether any of them is worth a click.
     */
    badgeDemo: 'Живое демо',
    badgeRepo: 'Код открыт',
    badgeWip: 'В разработке',

    /** Counted into the offer block above the grid. */
    servicesPlural: ['сервис', 'сервиса', 'сервисов'] as [string, string, string],

    /* Case-study dialog */
    dialogLabel: (project: string) => `Проект ${project}`,
    dialogClose: 'Закрыть',
    dialogShots: 'Как это выглядит',
    dialogHighlights: 'Что внутри',
    dialogYear: 'Год',
    dialogRole: 'Роль',
    dialogStack: 'Стек',
    dialogLinks: 'Можно проверить',
    dialogWrite: 'Обсудить похожую задачу',

    /* Mobile menu */
    menuOpen: 'Открыть меню',
    menuClose: 'Закрыть меню',

    /** Counter under each stack item: "3 проекта". */
    projectsPlural: ['проект', 'проекта', 'проектов'] as [string, string, string],

    /* Blog */
    blogOpen: (post: string) => `Читать: ${post}`,
    blogRead: 'Читать',
    blogBack: 'Все статьи',
    blogCoverAlt: (post: string) => `Обложка статьи «${post}»`,
    /** "7 минут чтения" — the estimate printed in the feed and under the title. */
    blogMinutesPlural: ['минута', 'минуты', 'минут'] as [string, string, string],
    blogReading: (minutes: string) => `${minutes} чтения`,
    /** Closes the article: the reader finished, so the ask goes right there. */
    blogOutro: 'Похожая задача — и вы застряли на том же месте?',
    blogOutroCta: 'Написать в Telegram',
    /** Words-per-minute used for the estimate. Russian prose, unhurried. */
    blogWpm: 140,

  },
};
